import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { Card } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import Chart from 'react-apexcharts';
import { ApexOptions } from 'apexcharts';
import { useSelector, useDispatch } from 'react-redux';
import { DatePicker, Row, Col, Modal } from 'antd';
import dayjs, { Dayjs } from 'dayjs';
import { all_routes } from '../../router/all_routes';
import { RootState } from '../../../core/data/redux/store';
import { fetchAdminReportSummaryApi, fetchAdminTopMealWiseSummaryApi } from '../../../core/data/services/reportService';
import { fetchReportsSuccess } from '../../../core/data/redux/reportSlice';

// Exact API shape only
type AdminSummary = {
  studentsPresent?: number;
  studentsAbsent?: number;
  studentOdCount?: number;
  editedCount?: number;
  schoolsReported?: number;
  schoolsNotReported?: number;
  teachersReported?: number;
  teachersNotReported?: number;
  totalSchools?: number;
  totalStudents?: number;
  totalTeachers?: number;
  totalMaleStudents?: number;
  totalFemaleStudents?: number;
  mdmEnrolledCount?: number;
  melasServedCount?: number;
  mealsNotServedCount?: number;
};

const AdminDashboard: React.FC = () => {
  const dispatch = useDispatch();
  const [isLoadingReports, setIsLoadingReports] = useState(false);

  // selection state
  const [selectedState, setSelectedState] = useState<string | number>('');
  const [selectedDistrict, setSelectedDistrict] = useState<string>('');
  const [selectedBlock, setSelectedBlock] = useState<string>('');
  const [selectedVillage, setSelectedVillage] = useState('');
  const [schoolsCount, setSchoolsCount] = useState<number>(0);
  const [activeAccordion, setActiveAccordion] = useState<string | null>(null);

  const user = useSelector((state: RootState) => state.user.Users);
  const reports = useSelector((state: RootState) => state.reports.reports);
  // hold summary response
  const [summary, setSummary] = useState<AdminSummary | null>(null);

  // NEW: rows for Top Meal Coverage API
  const [topMealRows, setTopMealRows] = useState<any[]>([]);

  // NEW: modal for "View all"
  const [showAllModal, setShowAllModal] = useState(false);

  // Role helpers
  const getUserRole = useCallback(() => {
    let role = user?.[0].role;
    return role;
  }, [user]);

  const isStateAdmin = useCallback(
    () => getUserRole() === 'state-admin' || getUserRole() === 'state Admin' || getUserRole() === 'State Admin',
    [getUserRole]
  );
  const isDistrictAdmin = useCallback(
    () => getUserRole() === 'district-admin' || getUserRole() === 'district Admin' || getUserRole() === 'District Admin',
    [getUserRole]
  );
  const isBlockAdmin = useCallback(
    () => getUserRole() === 'block-admin' || getUserRole() === 'block Admin' || getUserRole() === 'Block Admin',
    [getUserRole]
  );
  const isVillageAdmin = useCallback(
    () => getUserRole() === 'village-ward-admin' || getUserRole() === 'village ward Admin' || getUserRole() === 'Village Ward Admin',
    [getUserRole]
  );
  const isSuperAdmin = useCallback(
    () => getUserRole() === 'admin' || getUserRole() === 'Admin' || getUserRole() === 'Super Admin',
    [getUserRole]
  );

  useEffect(() => {
    const userEntity = user?.[0]?.relatedEntity;

    if (userEntity && isStateAdmin()) {
      setSelectedState(userEntity?.states?.id);
    }
    if (userEntity && isDistrictAdmin()) {
      setSelectedDistrict(userEntity?.districts?.id);
      setSelectedState(userEntity?.districts?.stateId);
    }
    if (userEntity && isBlockAdmin()) {
      setSelectedBlock(userEntity?.blockAreas?.id);
      setSelectedDistrict(userEntity?.blockAreas?.districtId);
      setSelectedState(userEntity?.blockAreas?.stateId);
    }
    if (userEntity && isVillageAdmin()) {
      setSelectedVillage(userEntity?.villageWards?.id);
      setSelectedBlock(userEntity?.villageWards?.blockAreaId);
      setSelectedDistrict(userEntity?.villageWards?.districtId);
      setSelectedState(userEntity?.villageWards?.stateId);
    }
  }, [user, isStateAdmin, isDistrictAdmin, isBlockAdmin, isVillageAdmin]);

  // date filters
  const [startDate, setStartDate] = useState<string>('2025-09-21');
  const [endDate, setEndDate] = useState<string>('2025-09-21');

  // fetch only the requested summary with fixed payload and API
  const fetchReports = useCallback(async () => {
    setIsLoadingReports(true);
    try {
      const payload: {
        startDate: string;
        endDate: string;
        filter: string;
        isCumulative: boolean;
      } = {
        startDate,
        endDate,
        filter: 'level|state',
        isCumulative: true,
      };

      if (selectedVillage) {
        payload.filter = 'level|school';
      } else if (selectedBlock) {
        payload.filter = 'level|village';
      } else if (selectedDistrict) {
        payload.filter = 'level|block';
      } else if (selectedState) {
        payload.filter = 'level|district';
      }

      const resp = await fetchAdminReportSummaryApi(payload);

      // Clear list-style reports; we’re using summary now
      dispatch(fetchReportsSuccess([]));

      // Service returns the summary object; set directly
      setSummary(resp || null);

      if (resp) {
        setSchoolsCount(resp.totalSchools || 0);
      }
    } catch (error) {
      dispatch(fetchReportsSuccess([]));
      console.error('Error fetching admin summary:', error);
      setSummary(null);
    } finally {
      setIsLoadingReports(false);
    }
  }, [dispatch, startDate, endDate, selectedState, selectedDistrict, selectedBlock, selectedVillage]);

  useEffect(() => {
    fetchReports();
  }, [fetchReports]);

  // NEW: fetch Top Meal Coverage using the provided API and payload
  const fetchTopMeals = useCallback(async () => {
    try {
      const payload: {
        startDate: string;
        endDate: string;
        filter: string;
        sortBy: string;
        order: 'ASC' | 'DESC';
      } = {
        startDate,
        endDate,
        filter: 'level|state',
        sortBy: 'melasNotServedCount',
        order: 'DESC',
      };

      if (selectedVillage) {
        payload.filter = 'level|school';
      } else if (selectedBlock) {
        payload.filter = 'level|village';
      } else if (selectedDistrict) {
        payload.filter = 'level|block';
      } else if (selectedState) {
        payload.filter = 'level|district';
      }

      const resp = await fetchAdminTopMealWiseSummaryApi(payload);
      // const rows = Array.isArray(resp?.data) ? resp.data : [];
      setTopMealRows(resp);
      console.log("Top meal wise summary", resp);
    } catch (e) {
      console.error('Error fetching top meals', e);
      setTopMealRows([]);
    }
  }, [startDate, endDate, selectedState, selectedDistrict, selectedBlock, selectedVillage]);

  useEffect(() => {
    fetchTopMeals();
  }, [fetchTopMeals]);

  // Level labels
  const currentLevel = useMemo(() => {
    if (selectedState && selectedDistrict && selectedBlock && selectedVillage) return 'school';
    if (selectedState && selectedDistrict && selectedBlock) return 'village';
    if (selectedState && selectedDistrict) return 'block';
    if (selectedState) return 'district';
    return 'state';
  }, [selectedState, selectedDistrict, selectedBlock, selectedVillage]);

  // Totals derived from summary; otherwise fallback to array logic
  const totals = useMemo(() => {
    if (summary) {
      const present = summary.studentsPresent || 0;
      const absent = summary.studentsAbsent || 0;

      const enrolment = (summary.mdmEnrolledCount ?? 0) || (summary.totalStudents ?? 0);

      const mealsServed = summary.melasServedCount ?? 0;
      const notServed = summary.mealsNotServedCount ?? Math.max(enrolment - mealsServed, 0);

      const totalSchools = summary.totalSchools || 0;
      const reportedSchools = summary.schoolsReported || 0;
      const notReportedSchools = summary.schoolsNotReported || 0;

      const totalTeachers = summary.totalTeachers || 0;
      const teachersReported = summary.teachersReported || 0;
      const teachersNotReported = summary.teachersNotReported || 0;

      const absentBase = present + absent;
      const absentPct = absentBase > 0 ? Math.round((absent / absentBase) * 100) : 0;
      const coveragePct = enrolment > 0 ? Math.round((mealsServed / enrolment) * 100) : 0;
      const inactiveSchoolsPct = totalSchools > 0 ? Math.round((notReportedSchools / totalSchools) * 100) : 0;

      return {
        totalSchools,
        reportedSchools,
        notReportedSchools,
        totalTeachers,
        teachersReported,
        teachersNotReported,
        totalStudents: enrolment,
        enrolment,
        present,
        absent,
        mealsServed,
        mealsNotServed: notServed,
        absentPct,
        coveragePct,
        inactiveSchoolsPct,
      };
    }

    // Fallback with array-based computations (if any)
    let totalSchools = 0;
    let reportedSchools = 0;
    let notReportedSchools = 0;
    let enrolment = 0;
    let mealsServed = 0;
    let totalStudents = 0;

    reports.forEach((item: any) => {
      totalSchools += item.totalSchools || 0;
      reportedSchools += item.schoolsReported || 0;
      notReportedSchools += item.schoolsNotReported || 0;
      enrolment += item.reportingSchoolsEnrolment || 0;
      mealsServed += item.melasServedCount || 0;
      totalStudents += item.totalStudents || 0;
    });

    const absent = enrolment - mealsServed;
    const absentPct = enrolment > 0 ? Math.round((absent / enrolment) * 100) : 0;
    const coveragePct = enrolment > 0 ? Math.round((mealsServed / enrolment) * 100) : 0;
    const inactiveSchoolsPct = totalSchools > 0 ? Math.round((notReportedSchools / totalSchools) * 100) : 0;

    return {
      totalSchools,
      reportedSchools,
      notReportedSchools,
      totalTeachers: 0,
      teachersReported: 0,
      teachersNotReported: 0,
      totalStudents: totalStudents || enrolment,
      enrolment: enrolment || totalStudents,
      present: 0,
      absent,
      mealsServed,
      mealsNotServed: Math.max((enrolment || totalStudents) - mealsServed, 0),
      absentPct,
      coveragePct,
      inactiveSchoolsPct,
    };
  }, [summary, reports]);

  // Build a single row for charts/cards from summary by computing needed percentages
  const listFromSummary = useMemo(() => {
    if (!summary) return [];

    const enrolment = (summary.mdmEnrolledCount ?? 0) || (summary.totalStudents ?? 0);

    const served = summary.melasServedCount ?? 0;

    const availedMealPct = enrolment > 0 ? Math.round((served / enrolment) * 100) : 0;

    const mealsNotServed = summary.mealsNotServedCount ?? Math.max(enrolment - served, 0);

    const mealsNotServedPct = enrolment > 0 ? Math.round((mealsNotServed / enrolment) * 100) : 0;

    const totalSchools = summary.totalSchools ?? 0;
    const notReported = summary.schoolsNotReported ?? 0;
    const schoolsNotReportedPct = totalSchools > 0 ? Math.round((notReported / totalSchools) * 100) : 0;

    const present = summary.studentsPresent ?? 0;
    const absent = summary.studentsAbsent ?? 0;
    const base = present + absent;
    const attendancePct = base > 0 ? Math.round((present / base) * 100) : 0;

    // Return a row the charts/cards expect (not the API model)
    return [
      {
        name: 'Overall',
        mdmEnrolledPct: attendancePct, // used as "Attendances (%)"
        availedMealPct,
        melasServedCount: served,
        totalStudents: enrolment,
        mdmEnrolledCount: enrolment, // for sidebar count
        mealsNotServedCount: mealsNotServed,
        mealsNotServedPct,
        schoolsNotReported: notReported,
        schoolsNotReportedPct,
      },
    ];
  }, [summary]);

  // NEW: map Top Meal API rows to the UI shape expected by the Top Meal Coverage section
  const topMealsMapped = useMemo(() => {
    return topMealRows.map((r: any) => {
      const enrolment = Number(r?.mdmEnrolledCount ?? r?.totalStudents ?? 0);
      const served = Number(r?.melasServedCount ?? r?.mealsServedCount ?? 0);
      const notServed = Number(r?.mealsNotServedCount ?? r?.melasNotServedCount ?? Math.max(enrolment - served, 0));

      const availedMealPct = Math.round(Number(r?.availedMealPct ?? (enrolment ? (served / enrolment) * 100 : 0)));
      const mealsNotServedPct = Math.round(Number(r?.mealsNotServedPct ?? (enrolment ? (notServed / enrolment) * 100 : 0)));
      const mdmEnrolledPct = Math.round(Number(r?.mdmEnrolledPct ?? 0));
      const schoolsNotReported = Number(r?.schoolsNotReported ?? 0);
      const schoolsNotReportedPct = Math.round(Number(r?.schoolsNotReportedPct ?? 0));

      return {
        name: r?.name ?? 'Unknown',
        mdmEnrolledPct,
        availedMealPct,
        melasServedCount: served,
        totalStudents: Number(r?.totalStudents ?? enrolment),
        mdmEnrolledCount: enrolment,
        mealsNotServedCount: notServed,
        mealsNotServedPct,
        schoolsNotReported,
        schoolsNotReportedPct,
        summaryDate: r?.summaryDate,
        id: r?.id,
        stateId: r?.stateId,
        totalFemaleStudents: r?.totalFemaleStudents,
        totalMaleStudents: r?.totalMaleStudents,
        totalMdmSchools: r?.totalMdmSchools,
        totalSchools: r?.totalSchools,
        totalTeachers: r?.totalTeachers
      };
    });
  }, [topMealRows]);



  // Collections for UI
  // UPDATED: Prefer Top Meal API for Top Meal Coverage; fallback to previous logic (slice 5 for dashboard)
  const topCoverage = useMemo(() => {
    if (topMealsMapped.length > 0) {
      return topMealsMapped;
    }
    if (reports && reports.length > 0) {
      const sorted = [...reports].sort(
        (a, b) => (b.availedMealPct || b.mealsServed || 0) - (a.availedMealPct || a.mealsServed || 0)
      );
      return sorted;
    }
    return listFromSummary;
  }, [topMealsMapped, reports, listFromSummary]);

  // NEW: all items for "View all" (no slice)
  const allCoverage = useMemo(() => {
    if (topMealsMapped.length > 0) {
      return topMealsMapped;
    }
    if (reports && reports.length > 0) {
      const sorted = [...reports].sort(
        (a, b) => (b.availedMealPct || b.mealsServed || 0) - (a.availedMealPct || a.mealsServed || 0)
      );
      return sorted;
    }
    return listFromSummary;
  }, [topMealsMapped, reports, listFromSummary]);




  const toggleAccordion = (key: string) => {
    setActiveAccordion(activeAccordion === key ? null : key);
  };

  const drillDownData = {};

  return (
    <div className="page-wrapper">
      <div className="content">
        <div className="d-md-flex d-block align-items-center justify-content-between mb-4">
          <div className="my-auto mb-2">
            <h3 className="page-title mb-1">{isStateAdmin()
              ? 'State Admin Dashboard'
              : isDistrictAdmin()
                ? 'District Admin Dashboard'
                : isBlockAdmin()
                  ? 'Block Admin Dashboard'
                  : isVillageAdmin()
                    ? 'Village Admin Dashboard'
                    : 'Super Admin Dashboard'}</h3>
          </div>
        </div>

        <div className="container-fluid">
          {/* Date Filter */}
          <div className="card mb-3">
            <div className="card-body">
              <div className="row align-items-center">
                <div className="col-md-9">
                  {startDate && endDate && (
                    <div>
                      <i className="ti ti-calendar me-2"></i>
                      <span className="text-muted">
                        Showing data from <b>{startDate}</b> to <b>{endDate}</b>
                      </span>
                    </div>
                  )}
                </div>
                <div className="col-md-3 text-end">
                  <DatePicker
                    value={startDate ? dayjs(startDate, 'YYYY-MM-DD') : null}
                    onChange={(date: Dayjs | null) => {
                      if (date) {
                        const d = dayjs(date).format('YYYY-MM-DD');
                        setStartDate(d);
                        setEndDate(d);
                      } else {
                        setStartDate('');
                        setEndDate('');
                      }
                    }}
                    style={{ width: '50%' }}
                  />
                </div>
              </div>
            </div>
          </div>

          {isLoadingReports ? (
            <div className="text-center p-4">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
              <p className="mt-2 mb-0">Loading dashboard data...</p>
            </div>
          ) : (
            <>
              {/* Attendance and Meals Cards */}
              <div className="row g-4 mb-4">
                <div className="col-xl-6 col-lg-6 col-md-12">
                  <Card className="h-100 shadow-sm">
                    <Card.Body className="d-flex align-items-center p-4">
                      <div className="me-4 flex-shrink-0">
                        <div
                          className="d-flex align-items-center justify-content-center bg-primary bg-opacity-10 rounded-circle"
                          style={{ width: '60px', height: '60px' }}
                        >
                          <i className="fas fa-clipboard-list text-primary" style={{ fontSize: '28px' }}></i>
                        </div>
                      </div>
                      <div className="flex-grow-1">
                        <h5 className="card-title mb-2">Attendance</h5>
                        <h3 className="text-success mb-0">
                          Present {(totals.present || 0).toLocaleString()} - Absent {(totals.absent || 0).toLocaleString()}
                        </h3>
                        <small className="text-muted">({totals.absentPct}% absent)</small>
                      </div>
                    </Card.Body>
                  </Card>
                </div>
                <div className="col-xl-6 col-lg-6 col-md-12">
                  <Card className="h-100 shadow-sm">
                    <Card.Body className="d-flex align-items-center p-4">
                      <div className="me-4 flex-shrink-0">
                        <div
                          className="d-flex align-items-center justify-content-center bg-success bg-opacity-10 rounded-circle"
                          style={{ width: '60px', height: '60px' }}
                        >
                          <i className="fas fa-utensils text-success" style={{ fontSize: '28px' }}></i>
                        </div>
                      </div>
                      <div className="flex-grow-1">
                        <h5 className="card-title mb-2">Meals</h5>
                        <h3 className="text-danger mb-0">
                          {(totals.mealsServed || 0).toLocaleString()} - {(totals.mealsNotServed || 0).toLocaleString()}
                        </h3>
                        <small className="text-muted">({totals.coveragePct}% coverage)</small>
                      </div>
                    </Card.Body>
                  </Card>
                </div>
              </div>

              {/* Schools, Teachers, Students Cards */}
              <div className="row g-4 mb-4">
                <div className="col-xl-4 col-lg-4 col-md-6 col-sm-12">
                  <Card className="h-100 shadow-sm">
                    <Card.Body className="text-center p-4">
                      <div className="mb-3">
                        <div
                          className="d-flex align-items-center justify-content-center bg-info bg-opacity-10 rounded-circle mx-auto"
                          style={{ width: '60px', height: '60px' }}
                        >
                          <i className="fas fa-school text-info" style={{ fontSize: '28px' }}></i>
                        </div>
                      </div>
                      <h5 className="card-title mb-2">Schools</h5>
                      <h3 className="mb-2">{(totals.totalSchools || 0).toLocaleString()}</h3>
                      <div className="text-danger">
                        <strong>
                          {(totals.reportedSchools || 0).toLocaleString()} - {(totals.notReportedSchools || 0).toLocaleString()}
                        </strong>
                        <small className="d-block">({totals.inactiveSchoolsPct}% inactive)</small>
                      </div>
                    </Card.Body>
                  </Card>
                </div>
                <div className="col-xl-4 col-lg-4 col-md-6 col-sm-12">
                  <Card className="h-100 shadow-sm">
                    <Card.Body className="text-center p-4">
                      <div className="mb-3">
                        <div
                          className="d-flex align-items-center justify-content-center bg-warning bg-opacity-10 rounded-circle mx-auto"
                          style={{ width: '60px', height: '60px' }}
                        >
                          <i className="fas fa-chalkboard-teacher text-warning" style={{ fontSize: '28px' }}></i>
                        </div>
                      </div>
                      <h5 className="card-title mb-2">Teachers</h5>
                      <h3 className="mb-2">{(totals.totalTeachers || 0).toLocaleString()}</h3>
                      <div className="text-danger">
                        <strong>
                          {(totals.teachersReported || 0).toLocaleString()} - {(totals.teachersNotReported || 0).toLocaleString()}
                        </strong>
                        <small className="d-block">
                          ({totals.totalTeachers > 0 ? Math.round(((totals.teachersNotReported || 0) / totals.totalTeachers) * 100) : 0}% inactive)
                        </small>
                      </div>
                    </Card.Body>
                  </Card>
                </div>
                <div className="col-xl-4 col-lg-4 col-md-12 col-sm-12">
                  <Card className="h-100 shadow-sm">
                    <Card.Body className="text-center p-4">
                      <div className="mb-3">
                        <div
                          className="d-flex align-items-center justify-content-center bg-secondary bg-opacity-10 rounded-circle mx-auto"
                          style={{ width: '60px', height: '60px' }}
                        >
                          <i className="fas fa-user-graduate text-secondary" style={{ fontSize: '28px' }}></i>
                        </div>
                      </div>
                      <h5 className="card-title mb-2">Students</h5>
                      <h3 className="mb-2">{(totals.totalStudents || 0).toLocaleString()}</h3>
                      <div className="text-danger">
                        <strong>
                          {(totals.present || 0).toLocaleString()} - {(totals.absent || 0).toLocaleString()}
                        </strong>
                        <small className="d-block">({totals.absentPct}% inactive)</small>
                      </div>
                    </Card.Body>
                  </Card>
                </div>
              </div>

              {/* Top Meal Coverage */}
              <div className="row mb-4 ">
                <div className="col-12">
                  <Card className="shadow-sm">
                    <Card.Header className="bg-white border-bottom">
                      <div className="d-flex align-items-center justify-content-between">
                        <h5 className="mb-0">
                          <i className="fas fa-chart-line me-2 text-primary"></i>
                          Top Meal Coverage
                        </h5>
                        {/* <button
                          type="button"
                          className="btn btn-link text-decoration-none p-0"
                          onClick={() => setShowAllModal(true)}
                        >
                          View all
                        </button> */}
                      </div>
                    </Card.Header>
                    <Card.Body className="p-4">
                      <Row gutter={[16, 16]} justify={topCoverage.length === 1 ? 'center' : 'start'}>
                        {topCoverage.map((item: any, index: number) => (
                          <Col key={`${ item?.name ?? 'Unknown' }-${ item?.summaryDate ?? index }`} xs={24} md={12} lg={8}>
                            <div className="border rounded p-3 h-100">
                              <div className="d-flex align-items-center justify-content-between">
                                <div className="d-flex align-items-center">
                                  <i className="fas fa-utensils text-primary me-3" style={{ fontSize: '24px' }}></i>
                                  <div>
                                    <h6 className="mb-0">{item.name || 'Overall'}</h6>
                                    <p>{`State Id :${ item.stateId }`}</p>
                                  </div>
                                </div>
                                <div className="text-end">
                                  <small className="mb-0">Enroll Count - {(item.mdmEnrolledCount || 0).toLocaleString()}</small>
                                  <span className="badge bg-success">{item.availedMealPct || 0}%</span>
                                </div>
                              </div>
                            </div>
                          </Col>
                        ))}
                        {topCoverage.length === 0 && (
                          <Col span={24}>
                            <div className="text-muted">No items to display</div>
                          </Col>
                        )}
                      </Row>
                    </Card.Body>
                  </Card>
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      {/* View All Modal */}

      {/* <Modal
        open={showAllModal}
        onCancel={() => setShowAllModal(false)}
        footer={null}
        title="All Meal Coverage"
        className=''
        width={800}
        centered
      >
        <Row gutter={[16, 16]}>
          {allCoverage.map((item: any, index: number) => (
            <Col key={`${ item?.name ?? 'Unknown' }-${ item?.summaryDate ?? index }`} xs={24} md={12} lg={8}>
              <div className="border rounded p-3 h-100">
                <div className="d-flex align-items-center justify-content-between">
                  <div className="d-flex align-items-center">
                    <i className="fas fa-utensils text-primary me-3" style={{ fontSize: '24px' }}></i>
                    <div>
                      <h6 className="mb-0">{item.name || 'Overall'}</h6>
                    </div>
                  </div>
                  <div className="text-end">
                    <h4 className="mb-0">{(item.mdmEnrolledCount || 0).toLocaleString()}</h4>
                    <span className="badge bg-success">{item.availedMealPct || 0}%</span>
                  </div>
                </div>
              </div>
            </Col>
          ))}
          {allCoverage.length === 0 && (
            <Col span={24}>
              <div className="text-muted">No items to display</div>
            </Col>
          )}
        </Row>
      </Modal> */}


    </div>
  );
};

export default AdminDashboard;
